import { createApi } from '@reduxjs/toolkit/query/react';
import { customBaseQuery } from '@/lib/baseQuery';
import { ApiResponse } from '@/shared/types';
import type {
  DeleteChunkResult,
  DeleteDocumentResult,
  IngestPayload,
  IngestResult,
  KbDocument,
  SearchRequest,
  SearchResult,
  UploadResult,
} from '../types/knowledgeBase';

export const knowledgeBaseApi = createApi({
  reducerPath: 'knowledgeBaseApi',
  baseQuery: customBaseQuery,
  tagTypes: ['KbDocuments'],
  endpoints: (builder) => ({
    // multipart/form-data — FormData body; fetchBaseQuery lets the browser set
    // the boundary. Field name must be `file` (server: FileInterceptor('file')).
    uploadKbFile: builder.mutation<UploadResult, FormData>({
      query: (formData) => ({
        url: '/v1/knowledge-base/upload',
        method: 'POST',
        body: formData,
      }),
      transformResponse: (r: ApiResponse<UploadResult>) => r.data,
    }),
    ingestDocument: builder.mutation<IngestResult, IngestPayload>({
      query: (body) => ({
        url: '/v1/knowledge-base',
        method: 'POST',
        body,
      }),
      transformResponse: (r: ApiResponse<IngestResult>) => r.data,
      // A new document changes the list → refetch it.
      invalidatesTags: ['KbDocuments'],
    }),
    // One row per document, newest-first (server-sorted by lastIndexedAt desc).
    getDocuments: builder.query<KbDocument[], void>({
      query: () => ({ url: '/v1/knowledge-base/documents' }),
      transformResponse: (r: ApiResponse<KbDocument[]>) => r.data,
      providesTags: ['KbDocuments'],
    }),
    // Delete a whole document (all its chunks). 404 if documentId is unknown.
    deleteDocument: builder.mutation<DeleteDocumentResult, string>({
      query: (documentId) => ({
        url: `/v1/knowledge-base/documents/${documentId}`,
        method: 'DELETE',
      }),
      transformResponse: (r: ApiResponse<DeleteDocumentResult>) => r.data,
      invalidatesTags: ['KbDocuments'],
    }),
    // Delete a single chunk by numeric id. 400 if not a positive int; 404 if missing.
    deleteChunk: builder.mutation<DeleteChunkResult, string>({
      query: (id) => ({
        url: `/v1/knowledge-base/chunks/${id}`,
        method: 'DELETE',
      }),
      transformResponse: (r: ApiResponse<DeleteChunkResult>) => r.data,
      invalidatesTags: ['KbDocuments'],
    }),
    // Semantic search — POST with a body, returns 200 with results best-first.
    // A mutation (not a query): it's an on-demand action and never mutates data,
    // so it neither provides nor invalidates the KbDocuments tag.
    searchKnowledgeBase: builder.mutation<SearchResult[], SearchRequest>({
      query: (body) => ({
        url: '/v1/knowledge-base/search',
        method: 'POST',
        body,
      }),
      transformResponse: (r: ApiResponse<SearchResult[]>) => r.data,
    }),
  }),
});

export const {
  useUploadKbFileMutation,
  useIngestDocumentMutation,
  useGetDocumentsQuery,
  useDeleteDocumentMutation,
  useDeleteChunkMutation,
  useSearchKnowledgeBaseMutation,
} = knowledgeBaseApi;
